import { css } from "@emotion/react";
import { Loading } from "@podtrac/common/components/Loading";
import { Suspense, useState } from "react";
import { FaBan, FaCheckSquare, FaQuestionCircle, FaSpinner } from "react-icons/fa";
import * as ReactModal from "react-modal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFeedHost, useFeedSearch } from "../api/feed-search";
import { ResultImage } from "./Search";
import { getHostMeta } from "./hosts";
import UnknownHost from "./hosts/UnknownHost";
import UnsupportedHost from "./hosts/UnsupportedHost";

export default function Host() {
    const [searchParams, setSearchParams] = useSearchParams();
    const url = searchParams.get("url");
    const query = searchParams.get("q");
    const h = useFeedHost(url),
        { isLoading: hostLoading, data: host = {} } = h;
    const f = useFeedSearch(query, url),
        { isLoading: feedLoading, data: { found = [] } = {} } = f,
        feed = found[0];
    return (
        <>
            <h1>Get Started Measuring Your Podcast Traffic and Demographics with Podtrac</h1>

            {feedLoading ? <Loading /> : feed ? <FeedResult feed={feed} /> : <p>Feed not found.</p>}

            {hostLoading ? (
                <h2>
                    <FaSpinner className="icon-spin" style={{ verticalAlign: "middle" }} />
                    <span style={{ marginLeft: "0.5rem" }}>Scanning feed...</span>
                </h2>
            ) : feed && host ? (
                <HostResult host={host} />
            ) : (
                <p>Error scanning feed.</p>
            )}
        </>
    );
}

export function FeedResult({ feed }) {
    return (
        feed && (
            <div
                style={{
                    display: "flex",
                    margin: "2rem 0"
                }}
            >
                <ResultImage feed={feed} size="4rem" marginRight="0.75rem" />

                <div>
                    <div
                        style={{
                            margin: "0.25rem 0",
                            fontWeight: "bold",
                            fontSize: "1.5em"
                        }}
                    >
                        {feed.title}
                    </div>
                    <div
                        style={{
                            marginBottom: "0.25rem",
                            maxWidth: "60ch",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            color: "#666"
                        }}
                        title={feed.url}
                    >
                        {feed.url}
                    </div>
                </div>
            </div>
        )
    );
}

const modalStyles = {
    content: {
        top: "10%",
        left: "10%",
        right: "10%",
        bottom: "auto"
    }
};

function HostResult({ host }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [modalOpen, setModalOpen] = useState(false);
    const navigate = useNavigate();
    const buttonClick = event =>
        navigate({ pathname: "/register/account", search: searchParams.toString() });
    const openModal = () => setModalOpen(true);
    const closeModal = () => setModalOpen(false);

    if (host.registeredAccountID)
        return (
            <>
                <h2>Already Registered</h2>
                <p>This show is already registered in an existing Podtrac account.</p>
                <p>
                    Email <a href="mailto:support@podtrac.com">support@podtrac.com</a> with the RSS
                    URL and show name for assistance.
                </p>
            </>
        );

    const meta = getHostMeta(host.host),
        name = meta.name ?? host.host;

    return (
        <>
            <h2>Supported Podtrac Services</h2>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {meta.supported == "YES" ? (
                    <Supported host={host.host} meta={meta} openModal={openModal} />
                ) : meta.supported == "NO" ? (
                    <Unsupported host={host.host} meta={meta} />
                ) : (
                    <Unknown meta={meta} />
                )}
                <li>
                    <h1>
                        <FaCheckSquare
                            style={{ fill: "green", fontSize: "1.25em", verticalAlign: "top" }}
                        />{" "}
                        Podcast Audience Demographics
                    </h1>
                    <p style={{ marginLeft: "2.5rem" }}>
                        Your show can use Podtrac’s Audience Demographics! Once you register by
                        clicking the button below, you’ll have access to your Podtrac Dashboard
                        where you’ll find your unique survey and information on how to implement the
                        survey.
                    </p>
                </li>
            </ul>
            <div style={{ textAlign: "center" }}>
                <button onClick={buttonClick} style={{ fontSize: "1.5rem", padding: "0.5rem" }}>
                    Register this show with Podtrac
                </button>
                <p style={{ margin: "1rem", fontSize: "0.75rem" }}>
                    You can add more shows after you create your account
                </p>
            </div>
            {/*<pre
                style={{ marginTop: "100vh", background: "#eee", padding: "1em", overflow: "auto" }}
            >
                {JSON.stringify({ host, meta }, null, 2)}
            </pre>*/}
            <ReactModal
                isOpen={modalOpen}
                onRequestClose={closeModal}
                style={modalStyles}
                contentLabel="How to Set Up Podtrac Measurement"
            >
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        & > h1 {
                            margin: 0.5rem 0;
                            font-size: 1.5rem;
                            font-weight: 500;
                            color: #000;
                            & span {
                                opacity: 0.65;
                                display: inline-block;
                                font-size: 0.8em;
                                margin-left: 1em;
                                text-transform: uppercase;
                            }
                            & strong {
                                display: inline-block;
                                margin-left: 0.5em;
                            }
                        }
                        margin-bottom: 1rem;
                    `}
                >
                    <h1
                        css={css`
                            flex: 1;
                        `}
                    >
                        How to Set Up Podtrac Measurement <span>on {name}</span>
                    </h1>
                    <div
                        onClick={closeModal}
                        css={css`
                            font-size: 1.5rem;
                            font-weight: 500;
                            color: #999;
                            cursor: pointer;
                            &:hover {
                                color: #000;
                            }
                        `}
                    >
                        ×
                    </div>
                </div>
                <div
                    css={css`
                        max-height: 70vh;
                        overflow: auto;
                        padding-right: 0.5rem;
                    `}
                >
                    <Suspense fallback="Loading...">
                        {meta.instructions ? <meta.instructions host={host} /> : null}
                    </Suspense>
                </div>
            </ReactModal>
        </>
    );
    return (
        <>
            <p>Host: {meta.name}</p>
            <p>
                <meta.ShortInstructions />
            </p>
            <div style={{ textAlign: "center" }}>
                <button onClick={buttonClick}>Register this show with Podtrac</button>
                <p style={{ margin: "1rem", fontSize: "0.75rem" }}>
                    You can add more shows after you create your account
                </p>
            </div>
        </>
    );
}

function Supported({ host, meta: { name }, openModal }) {
    return (
        <li>
            <h1>
                <FaCheckSquare
                    style={{ fill: "green", fontSize: "1.25em", verticalAlign: "top" }}
                />{" "}
                Podcast Traffic Measurement
            </h1>
            <div style={{ marginLeft: "2.5rem" }}>
                <p>
                    Your show is hosted on <b>{name ?? host}</b>. Once you register using the button
                    below, we will walk you through{" "}
                    <a
                        href="#"
                        onClick={event => {
                            event.preventDefault();
                            openModal();
                        }}
                    >
                        how to set up Podtrac measurement on {name ?? host}
                    </a>
                    .
                </p>
            </div>
        </li>
    );
}

function Unsupported({ host, meta: { name } }) {
    return (
        <li>
            <h1>
                <FaBan style={{ fill: "#c00", fontSize: "1.25em", verticalAlign: "top" }} /> Podcast
                Traffic Measurement
            </h1>
            <div style={{ marginLeft: "2.5rem" }}>
                <UnsupportedHost name={name} />
            </div>
        </li>
    );
}

function Unknown({ meta }) {
    return (
        <li>
            <h1>
                <FaQuestionCircle
                    style={{ fill: "gray", fontSize: "1.25em", verticalAlign: "top" }}
                />{" "}
                Podcast Traffic Measurement
            </h1>
            <div style={{ marginLeft: "2.5rem" }}>
                <UnknownHost />
            </div>
        </li>
    );
}
